import { encryptPayload, decryptPayload } from './crypto';
import { registerPreparedRetentionReconciler, setPreparedChartPin } from './preparedChartStore';
import { getOfflineOwnerIdentity } from './offlineIdentity';

const DB_NAME = 'vela-chart-drafts';
const DB_VERSION = 1;
const STORE = 'drafts';

function openDb() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const store = db.createObjectStore(STORE, { keyPath: 'draftId' });
        store.createIndex('updatedAt', 'updatedAt');
        store.createIndex('ownerKey', 'ownerKey');
      }
    };
    request.onsuccess = () => { request.result.onversionchange = () => request.result.close(); resolve(request.result); };
    request.onerror = () => reject(request.error);
  });
}

function transact(mode, operation) {
  return openDb().then((db) => new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, mode);
    const result = operation(tx.objectStore(STORE));
    tx.oncomplete = () => { db.close(); resolve(result); };
    tx.onerror = () => { db.close(); reject(tx.error); };
    tx.onabort = () => { db.close(); reject(tx.error || new Error('Device save interrupted.')); };
  }));
}

const uuid = () => crypto.randomUUID
  ? crypto.randomUUID()
  : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

async function requestKey(input) {
  let identity = '';
  try {
    const body = JSON.parse(input.body || '{}');
    identity = body.clientRequestId || body.id || body.Id || body.referenceId || body.ReferenceId
      || `${body.visitDate || body.VisitDate || ''}:${body.babyReferenceId || body.BabyReferenceId || ''}`;
  } catch {}
  const bytes = new TextEncoder().encode(`${input.method}:${input.url}:${identity}`);
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  return Array.from(new Uint8Array(digest)).map((byte) => byte.toString(16).padStart(2, '0')).join('');
}

export function getOfflineOwnerKey() {
  const user = localStorage.getItem('userId') || 'unknown-user';
  let organization = 'unknown-organization';
  try {
    const token = localStorage.getItem('velaToken');
    if (token) {
      const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
      organization = payload.contextReferenceId || payload.organizationReferenceId || payload.organizationId || organization;
    }
  } catch {}
  return `${organization}:${user}`;
}

async function pinDraft(record, pinned = true) {
  if (!record?.clientReferenceId || record.ownerKey !== getOfflineOwnerKey()) return;
  await setPreparedChartPin({ clientReferenceId: record.clientReferenceId }, `draft:${record.draftId}`, pinned);
}

export async function reconcileChartDraftPins(owner = getOfflineOwnerIdentity()) {
  if (!owner) return;
  const legacyOwnerKey = `${owner.contextReferenceId}:${owner.userReferenceId}`;
  const records = (await listChartDrafts({ includeOtherOwners: true })).filter(record => record.ownerKey === legacyOwnerKey);
  // Only add discovered pins here. A stale read must never release a pin for
  // work saved concurrently by another tab.
  const retained = records.filter(item => item.status !== 'synced');
  for (const record of retained) {
    if (record.clientReferenceId) await setPreparedChartPin({ clientReferenceId: record.clientReferenceId }, `draft:${record.draftId}`, true, owner);
  }
  if (retained.some(record => !record.clientReferenceId)) {
    throw new Error('Review the older device drafts before clearing prepared charts; their client could not be identified safely.');
  }
}

registerPreparedRetentionReconciler('legacy-drafts', reconcileChartDraftPins);

export async function saveChartDraft(input) {
  const now = new Date().toISOString();
  const ownerKey = getOfflineOwnerKey();
  const key = await requestKey(input);
  const headers = new Headers(input.headers || {});
  headers.delete('Authorization');
  headers.delete('Cookie');
  const payload = await encryptPayload({
    url: input.url, method: input.method,
    headers: Object.fromEntries(headers.entries()), body: input.body,
  });
  if (ownerKey !== getOfflineOwnerKey()) throw new Error('Practice access changed while saving.');
  // Every write has its own temporary pin. Releasing an acknowledged draft's
  // pin cannot unpin a newer write in this or another tab.
  const writePin = { ownerKey, clientReferenceId: input.clientReferenceId, draftId: `write:${uuid()}` };
  await pinDraft(writePin);
  let record;
  try {
  const db = await openDb();
  record = await new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    const store = tx.objectStore(STORE);
    let saved;
    const request = store.getAll();
    request.onsuccess = () => {
      const related = (request.result || []).filter(item => item.ownerKey === ownerKey && item.requestKey === key);
      const predecessor = related.find(item => item.sealedAt && item.status !== 'synced'
        && item.draftId !== input.reviewedReplacementOf);
      // Never mutate content once a request may have reached the server. The
      // predecessor keeps its original idempotency identity until acknowledged.
      const existing = input.coalesce === false ? null : related.find(item => !item.sealedAt
        && !['conflict', 'synced'].includes(item.status));
      const requested = (request.result || []).find(item => item.draftId === input.draftId);
      const recoveryOnly = Boolean(input.recoveryOnly || predecessor || existing?.recoveryOnly);
      saved = {
        draftId: existing?.draftId || (input.draftId && !requested ? input.draftId : uuid()),
        ownerKey, requestKey: key,
        chartType: input.chartType || 'clinical-chart',
        requestMethod: input.method || existing?.requestMethod || null,
        clientLabel: input.clientLabel || 'Clinical chart',
        clientReferenceId: input.clientReferenceId || existing?.clientReferenceId || null,
        pregnancyReferenceId: input.pregnancyReferenceId || existing?.pregnancyReferenceId || null,
        pregnancyId: input.pregnancyId || existing?.pregnancyId || null,
        chartId: input.chartId || existing?.chartId || null,
        baseVersion: existing?.baseVersion || input.baseVersion || null,
        createdAt: existing?.createdAt || input.createdAt || now,
        locallySavedAt: now, updatedAt: now,
        status: recoveryOnly ? 'recovery-only' : input.status || 'saved-on-device',
        recoveryOnly, predecessorDraftId: predecessor?.draftId || null,
        attemptCount: existing?.attemptCount || 0, nextAttemptAt: null,
        errorCategory: recoveryOnly ? 'review-required' : input.errorCategory || null,
        errorMessage: recoveryOnly
          ? 'Your changes are kept on this device. Review them against the server chart before saving.'
          : input.errorMessage || null,
        serverChartId: input.serverChartId || null,
        legacyRequestId: input.legacyRequestId || existing?.legacyRequestId || null,
        reviewedAt: null, payload,
      };
      store.put(saved);
    };
    tx.oncomplete = () => resolve(saved);
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error || new Error('Device save interrupted.'));
  }).finally(() => db.close());
  await pinDraft(record);
  } finally {
    // If the durable write succeeded but its final pin failed, retain the
    // temporary pin. A storage error must not make unsynced work evictable.
    if (!record || !record.clientReferenceId) await pinDraft(writePin, false);
  }
  await pinDraft(writePin, false);
  window.dispatchEvent(new CustomEvent('vela:chart-drafts-changed'));
  return record;
}

// IndexedDB serializes this claim across tabs. A crash releases the claim after
// its lease, but the sealed request body and mutation ID never change.
export async function claimChartDraft(draftId, { retryActionRequired = false } = {}) {
  const ownerKey = getOfflineOwnerKey();
  const db = await openDb();
  const claimed = await new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    const store = tx.objectStore(STORE);
    let result = null;
    const request = store.get(draftId);
    request.onsuccess = () => {
      const record = request.result;
      if (!record || record.ownerKey !== ownerKey || record.recoveryOnly) return;
      const eligible = ['saved-on-device', 'syncing'].includes(record.status)
        || (retryActionRequired && record.status === 'action-required');
      if (!eligible || record.errorCategory === 'authorization') return;
      if (record.claimExpiresAt > Date.now() || Date.parse(record.nextAttemptAt) > Date.now()) return;
      result = { ...record, status: 'syncing', sealedAt: record.sealedAt || new Date().toISOString(),
        claimExpiresAt: Date.now() + 60000, errorCategory: null, errorMessage: null };
      store.put(result);
    };
    tx.oncomplete = () => resolve(result);
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error || new Error('Device claim interrupted.'));
  }).finally(() => db.close());
  if (!claimed || ownerKey !== getOfflineOwnerKey()) return null;
  return { ...claimed, request: await decryptPayload(claimed.payload) };
}

export async function listChartDrafts({ includeOtherOwners = false } = {}) {
  const db = await openDb();
  const records = await new Promise((resolve, reject) => {
    const request = db.transaction(STORE, 'readonly').objectStore(STORE).getAll();
    request.onsuccess = () => resolve(request.result || []);
    request.onerror = () => reject(request.error);
  }).finally(() => db.close());
  const ownerKey = getOfflineOwnerKey();
  return records
    .filter((record) => includeOtherOwners || record.ownerKey === ownerKey)
    .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
}

export async function readChartDraft(draftId) {
  const db = await openDb();
  const record = await new Promise((resolve, reject) => {
    const request = db.transaction(STORE, 'readonly').objectStore(STORE).get(draftId);
    request.onsuccess = () => resolve(request.result || null);
    request.onerror = () => reject(request.error);
  }).finally(() => db.close());
  if (!record) return null;
  if (record.ownerKey !== getOfflineOwnerKey()) return null;
  return { ...record, request: await decryptPayload(record.payload) };
}

export async function updateChartDraft(draftId, changes) {
  const current = await readChartDraft(draftId);
  if (!current) return null;
  const { request, ...record } = current;
  if (record.recoveryOnly && ['syncing', 'synced', 'saved-on-device'].includes(changes.status)) {
    throw new Error('This retained chart needs review; it cannot be replayed automatically.');
  }
  const updated = { ...record, ...changes, claimExpiresAt: changes.status === 'syncing' ? record.claimExpiresAt : null, updatedAt: new Date().toISOString() };
  await transact('readwrite', (store) => store.put(updated));
  await pinDraft(updated, updated.status !== 'synced');
  window.dispatchEvent(new CustomEvent('vela:chart-drafts-changed'));
  return updated;
}

export async function removeChartDraft(draftId) {
  const current = await readChartDraft(draftId);
  if (!current) return;
  await transact('readwrite', (store) => store.delete(draftId));
  await pinDraft(current, false);
  window.dispatchEvent(new CustomEvent('vela:chart-drafts-changed'));
}

export async function exportChartDraft(draftId) {
  const draft = await readChartDraft(draftId);
  if (!draft) throw new Error('Draft not found');
  return JSON.stringify({
    format: 'sena-encrypted-chart-draft-v1',
    exportedAt: new Date().toISOString(),
    metadata: Object.fromEntries(Object.entries(draft).filter(([key]) => !['request', 'payload'].includes(key))),
    encryptedPayload: draft.payload,
  }, null, 2);
}
